import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiFetch, setToken, getToken } from "../utils/api";

export type Whois =
  | "admin"
  | "member"
  | "probationer"
  | "student"
  | "graduate"
  | "executive"
  | "assessor";

export type RoleWithPermissions = {
  name: string;
  permissions?: string[];
};

export type User = {
  id?: number;
  email: string;
  phone?: string;
  phone_number?: string;
  user_category_id?: number | null;
  member_id?: string | number | null;
  membership_no?: string | null;
  name?: string;
  pro_pic?: string;
  updated_profile?: number | boolean;
  whois: Whois;

  roles: Array<RoleWithPermissions | string>;
  permissions: string[];

  onboardingComplete: boolean;

  profile?: {
    fullName?: string;
    phone?: string;
    department?: string;
  };

  tempPassword?: string;
  token?: string;
};

export type AuthContextType = {
  user: User | null;
  loading: boolean;

  activeRoleName: string | null;
  setActiveRole: (name: string) => void;

  // legacy compatibility (you may not use them now)
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (
    email: string,
    password: string,
    whois: Exclude<Whois, "admin">,
  ) => Promise<User>;

  signInAsAdmin: (email: string, password: string) => Promise<User>;
  signInWithToken: (email: string, token: string) => User;

  beginImpersonation: (token: string, user: any) => User;
  exitImpersonation: () => void;

  signOut: () => void;

  completeOnboarding: (
    profile: NonNullable<User["profile"]>,
  ) => Promise<User | null>;

  verifyTempPassword: (password: string) => Promise<boolean>;
  resendTempPassword: () => Promise<string | null>;

  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (perms: string[]) => boolean;

  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;

  /** Scoped to the role selected in the topbar — what the sidebar draws with. */
  hasActiveRolePermission: (perm: string) => boolean;

  refreshFromStorage: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const STORAGE_KEY = "logbook_auth_user_v2";
export const ACTIVE_ROLE_KEY = "niqs_active_role";
// Holds the admin's own session while they are "logged in as" another user,
// so they can return to it via exitImpersonation().
export const IMPERSONATOR_KEY = "niqs_impersonator_backup";

const norm = (v: any) =>
  String(v ?? "")
    .toLowerCase()
    .trim();
const normList = (arr?: any[]) => (arr || []).map(norm).filter(Boolean);

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function getRoleName(r: RoleWithPermissions | string): string {
  return typeof r === "string" ? r : r.name;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRoleName, setActiveRoleState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ROLE_KEY),
  );

  /**
   * READ-ONLY hydrate from localStorage.
   * RTK Query is the writer. AuthContext is the reader.
   */
  const readStorage = useCallback(() => {
    const parsed = safeParse<User>(localStorage.getItem(STORAGE_KEY));
    setUser(parsed);

    // Sync active role from storage, or auto-init from first role
    const stored = localStorage.getItem(ACTIVE_ROLE_KEY);
    if (stored) {
      setActiveRoleState(stored);
    } else if (parsed?.roles?.length) {
      const name = getRoleName(parsed.roles[0]);
      if (name) {
        localStorage.setItem(ACTIVE_ROLE_KEY, name);
        setActiveRoleState(name);
      }
    }

    const token = parsed?.token || localStorage.getItem("token");
    setToken(token || null);
  }, []);

  useEffect(() => {
    // initial hydrate
    readStorage();
    setLoading(false);

    // other tabs
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === STORAGE_KEY ||
        e.key === "token" ||
        e.key === ACTIVE_ROLE_KEY
      )
        readStorage();
    };

    // same tab
    const onAuthUpdated = () => readStorage();

    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:updated", onAuthUpdated as any);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:updated", onAuthUpdated as any);
    };
  }, [readStorage]);

  const refreshFromStorage = useCallback(() => {
    readStorage();
  }, [readStorage]);

  const setActiveRole = useCallback((name: string) => {
    localStorage.setItem(ACTIVE_ROLE_KEY, name);
    setActiveRoleState(name);
    window.dispatchEvent(new Event("auth:updated"));
  }, []);

  /* -------------------------
    legacy methods (safe)
  ------------------------- */

  const signIn = useCallback(async (email: string) => {
    const next: User = {
      email,
      whois: "probationer",
      roles: [],
      permissions: [],
      onboardingComplete: false,
    };
    setUser(next);
    return next;
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, whois: Exclude<Whois, "admin">) => {
      const created: User = {
        email,
        whois,
        roles: [],
        permissions: [],
        onboardingComplete: false,
        tempPassword: password,
      };
      setUser(created);
      return created;
    },
    [],
  );

  const signInAsAdmin = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<{
      token: string;
      user: {
        id: number;
        name?: string;
        email: string;
        whois: Whois;
        roles: Array<RoleWithPermissions | string>;
        permissions: string[];
      };
    }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });

    if (!res?.token || !res?.user) throw new Error("Invalid credentials");

    const loggedIn: User = {
      id: res.user.id,
      email: res.user.email,
      name: res.user.name,
      whois: res.user.whois,
      roles: res.user.roles || [],
      permissions: res.user.permissions || [],
      onboardingComplete: true,
      token: res.token,
    };

    localStorage.setItem("token", res.token);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loggedIn));

    // Set active role to first role
    if (loggedIn.roles.length) {
      const name = getRoleName(loggedIn.roles[0]);
      localStorage.setItem(ACTIVE_ROLE_KEY, name);
      setActiveRoleState(name);
    }

    window.dispatchEvent(new Event("auth:updated"));

    setToken(res.token);
    setUser(loggedIn);

    return loggedIn;
  }, []);

  const signInWithToken = useCallback((email: string, token: string) => {
    const u: User = {
      email,
      whois: "admin",
      roles: [],
      permissions: [],
      onboardingComplete: true,
      token,
    };

    localStorage.setItem("token", token);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    window.dispatchEvent(new Event("auth:updated"));

    setToken(token);
    setUser(u);
    return u;
  }, []);

  // Write a fresh session (token + user) into all the places the app reads it.
  const applySession = useCallback((tokenStr: string, apiUser: any): User => {
    const loggedIn: User = {
      id: apiUser.id,
      email: apiUser.email,
      name: apiUser.name,
      whois: apiUser.whois,
      roles: apiUser.roles || [],
      permissions: apiUser.permissions || [],
      onboardingComplete: true,
      token: tokenStr,
    };

    localStorage.setItem("token", tokenStr);
    setToken(tokenStr); // utils/api -> api_token_v1 (used by apiFetch)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loggedIn));

    if (loggedIn.roles.length) {
      const name = getRoleName(loggedIn.roles[0]);
      localStorage.setItem(ACTIVE_ROLE_KEY, name);
      setActiveRoleState(name);
    } else {
      localStorage.removeItem(ACTIVE_ROLE_KEY);
      setActiveRoleState(null);
    }

    window.dispatchEvent(new Event("auth:updated"));
    setUser(loggedIn);
    return loggedIn;
  }, []);

  // "Log in as" another user. The caller fetches the impersonation token/user
  // (via RTK) and hands them here; we back up the current admin session first so
  // it can be restored via exitImpersonation().
  const beginImpersonation = useCallback((tokenStr: string, apiUser: any) => {
    // Capture the real admin session only once (nested impersonation still
    // returns straight to the admin, not to a previous target).
    if (!localStorage.getItem(IMPERSONATOR_KEY)) {
      localStorage.setItem(IMPERSONATOR_KEY, JSON.stringify({
        token:    localStorage.getItem("token"),
        apiToken: getToken(),
        user:     localStorage.getItem(STORAGE_KEY),
        role:     localStorage.getItem(ACTIVE_ROLE_KEY),
        label:    user?.name || user?.email || "administrator",
      }));
    }
    return applySession(tokenStr, apiUser);
  }, [user, applySession]);

  // Return to the admin's own session.
  const exitImpersonation = useCallback(() => {
    const raw = localStorage.getItem(IMPERSONATOR_KEY);
    if (!raw) return;

    let backup: any;
    try { backup = JSON.parse(raw); } catch { localStorage.removeItem(IMPERSONATOR_KEY); return; }

    if (backup.token) localStorage.setItem("token", backup.token);
    else localStorage.removeItem("token");
    setToken(backup.apiToken || backup.token || null);

    if (backup.user) localStorage.setItem(STORAGE_KEY, backup.user);
    else localStorage.removeItem(STORAGE_KEY);

    if (backup.role) { localStorage.setItem(ACTIVE_ROLE_KEY, backup.role); setActiveRoleState(backup.role); }
    else { localStorage.removeItem(ACTIVE_ROLE_KEY); setActiveRoleState(null); }

    localStorage.removeItem(IMPERSONATOR_KEY);

    let restored: User | null = null;
    try { restored = backup.user ? JSON.parse(backup.user) : null; } catch { restored = null; }
    setUser(restored);
    window.dispatchEvent(new Event("auth:updated"));
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ROLE_KEY);
    localStorage.removeItem(IMPERSONATOR_KEY);
    setToken(null);
    setUser(null);
    setActiveRoleState(null);
    window.dispatchEvent(new Event("auth:updated"));
  }, []);

  const completeOnboarding = useCallback(
    async (profile: NonNullable<User["profile"]>) => {
      let updated: User | null = null;
      setUser((prev) => {
        updated = prev ? { ...prev, profile, onboardingComplete: true } : prev;
        return updated;
      });
      return updated;
    },
    [],
  );

  const verifyTempPassword = useCallback(
    async (password: string) => {
      if (!user) return false;
      const ok = user.tempPassword === password;
      if (ok) setUser({ ...user, tempPassword: undefined });
      return ok;
    },
    [user],
  );

  const resendTempPassword = useCallback(async () => {
    if (!user) return null;
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const next = Array.from({ length: 12 })
      .map(() => chars[Math.floor(Math.random() * chars.length)])
      .join("");
    setUser({ ...user, tempPassword: next });
    return next;
  }, [user]);

  const hasPermission = useCallback(
    (perm: string) => {
      // super_admin holds everything.
      //
      // RequirePermission (the route guard) has always bypassed for
      // super_admin, but this — which the sidebar and every AccessGate use —
      // did not. The result was a super_admin who could open a page by typing
      // its URL but could not SEE it in the menu, and whose action buttons were
      // hidden on it. Board result approval was the case that surfaced it.
      //
      // Matched here rather than at each call site so the two guards cannot
      // disagree again.
      const roleNames = normList((user?.roles || []).map(getRoleName));
      if (roleNames.includes("super_admin") || roleNames.includes("super admin")) {
        return true;
      }

      // Deliberately the MERGED list, not the active role's own.
      //
      // This is what route guards and AccessGate ask, and they answer "may
      // this person do it", which does not change because they switched the
      // chip in the topbar. Narrowing here would make a page the user just
      // opened start hiding its buttons. The sidebar asks a different
      // question — "what does the role I am currently wearing look like" —
      // and uses hasActiveRolePermission below.
      return normList(user?.permissions || []).includes(norm(perm));
    },
    [user],
  );

  /**
   * Does the ACTIVE role carry this permission?
   *
   * The sidebar draws the portal for one role at a time, so it needs the
   * privileges of that role alone rather than everything the account can do.
   *
   * A role that carries an EMPTY list carries nothing, and that is the answer
   * — not a reason to look at the merged list.
   *
   * This used to fall back to the merged list whenever the active role's own
   * list was empty, on the reasoning that bare roles (grade roles like
   * `fellow`, office roles like `president`) would otherwise produce a blank
   * sidebar. They do not: the screens those roles reach are gated by role
   * NAME, not by privilege, so they are unaffected by this function. What the
   * fallback actually did was hand a bare role every privilege the account
   * held anywhere — so a Chief Examiner who switched the chip to `fellow`
   * still passed `exam.access`, and the secretariat's Administration section
   * opened under a grade role that has no business in it.
   *
   * The fallback survives for the one case it was really written for: a
   * payload that says nothing about the active role's own privileges — roles
   * sent as bare names by an older API, a localStorage session that predates
   * the per-role shape, or a stored role name that names no role still held.
   * There the choice is between the merged list and a sidebar with no
   * privileged screen in it at all, and nothing is known either way.
   */
  const hasActiveRolePermission = useCallback(
    (perm: string) => {
      const activeIsSuperAdmin =
        !!activeRoleName &&
        ["super_admin", "super admin"].includes(norm(activeRoleName));

      if (activeIsSuperAdmin) return true;

      const roleObj = (user?.roles || []).find(
        (r): r is RoleWithPermissions =>
          typeof r !== "string" && norm(r?.name) === norm(activeRoleName),
      );

      // Array.isArray, not a truthiness/length test: `[]` is the role saying
      // it holds nothing, which is information. Only an ABSENT list — a bare
      // string role, or no matching role at all — is the unknown case.
      const scoped = roleObj?.permissions;
      if (Array.isArray(scoped)) {
        return normList(scoped).includes(norm(perm));
      }

      return normList(user?.permissions || []).includes(norm(perm));
    },
    [user, activeRoleName],
  );

  const hasAnyPermission = useCallback(
    (perms: string[]) => normList(perms).some((p) => hasPermission(p)),
    [hasPermission],
  );

  const hasRole = useCallback(
    (role: string) => {
      const roleNames = (user?.roles || []).map(getRoleName);
      return normList(roleNames).includes(norm(role));
    },
    [user],
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => normList(roles).some((r) => hasRole(r)),
    [hasRole],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      activeRoleName,
      setActiveRole,
      signIn,
      signUp,
      signInAsAdmin,
      signInWithToken,
      beginImpersonation,
      exitImpersonation,
      signOut,
      completeOnboarding,
      verifyTempPassword,
      resendTempPassword,
      hasPermission,
      hasAnyPermission,
      hasActiveRolePermission,
      hasRole,
      hasAnyRole,
      refreshFromStorage,
    }),
    [
      user,
      loading,
      activeRoleName,
      setActiveRole,
      signIn,
      signUp,
      signInAsAdmin,
      signInWithToken,
      beginImpersonation,
      exitImpersonation,
      signOut,
      completeOnboarding,
      verifyTempPassword,
      resendTempPassword,
      hasPermission,
      hasAnyPermission,
      hasActiveRolePermission,
      hasRole,
      hasAnyRole,
      refreshFromStorage,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

/* Guards (unchanged) */
export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();
  if (loading) return null;

  if (!user) {
    if (typeof window !== "undefined") window.location.href = "/login";
    return null;
  }
  return <>{children}</>;
};

export const RequireProfile: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();
  if (loading) return null;

  if (!user) return <>{children}</>;

  const isOnboarding =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/onboarding");

  if (!user.onboardingComplete && !isOnboarding) {
    window.location.href = "/onboarding";
    return null;
  }

  return <>{children}</>;
};

export const RequireAdmin: React.FC<{
  permission?: string;
  children: React.ReactNode;
}> = ({ permission = "admin.access", children }) => {
  const { user, loading, hasPermission } = useAuth();
  if (loading) return null;

  if (!user) {
    window.location.href = "/admin/login";
    return null;
  }

  if (!hasPermission(permission)) {
    window.location.href = "/403";
    return null;
  }

  return <>{children}</>;
};
